import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { games, getGameColor } from '../data/games';
import { getShare } from '../services/shareService';
import { stashInvite } from '../hooks/useInviteAcceptance';
import { formatScoreForCard } from '../utils/shareCard';
import SectionBlock from './SectionBlock';

/**
 * What someone sees when they follow a share link.
 *
 * Served at the same URL the crawler read (D47): the Function returns this
 * SPA's document with the share's meta tags substituted, then React boots here
 * and fetches **live** data. The stored share carries no scores at all (D45),
 * so a link opened three days later shows what that player is doing now.
 *
 * Copy and layout are explicitly not finalised - this is something sensible to
 * iterate on, not a settled design.
 *
 * Arrival stashes the share id but creates nothing (D48). The connection is
 * made by useInviteAcceptance the moment this visitor first has a record of
 * their own, whether that's from playing a game or signing up.
 */
function ShareLander() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (shareId) stashInvite(shareId);

    getShare(shareId)
      .then(setData)
      .catch((err) => {
        console.log('Error loading share:', err);
        setError('This link is no longer available.');
      })
      .finally(() => setIsLoading(false));
  }, [shareId]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-1">
        <SectionBlock title="Loading">
          <p className="px-3 py-6 text-sm text-nlg-muted text-center">One moment…</p>
        </SectionBlock>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto px-1">
        <SectionBlock title="Nice Light Games">
          <div className="px-3 py-4 text-sm text-nlg-muted">
            <p className="font-display font-black text-xl leading-tight text-nlg-cream mb-2">
              That link has expired.
            </p>
            <p className="mb-3">
              No matter — the games are all still here, and they reset every day.
            </p>
            <Link to="/" className="font-bold text-nlg-gold">Have a look around →</Link>
          </div>
        </SectionBlock>
      </div>
    );
  }

  const name = data.user?.username || 'A friend';
  const scores = data.user?.scores || [];

  const scoredIds = new Set(scores.map((s) => String(s.gameId)));
  // What they've got on their list but haven't played yet today. Referenced
  // here rather than on the card, which shows only what's been scored (D46).
  const stillToPlay = (data.user?.playlist || [])
    .filter((id) => !scoredIds.has(String(id)))
    .map((id) => games.find((g) => g.id === id))
    .filter(Boolean)
    .slice(0, 5);

  return (
    <div className="max-w-4xl mx-auto px-1">
      <SectionBlock title={scores.length > 0 ? "Today's round" : 'An invitation'}>
        <div className="px-3 py-3">
          <p className="font-display font-black text-2xl leading-tight text-nlg-cream mb-1">
            {scores.length > 0
              ? `${name} played today.`
              : `${name} wants to play with you.`}
          </p>
          <p className="text-sm text-nlg-muted mb-3">
            A daily round of puzzles in one place — for the company, not the competition.
          </p>

          {scores.length > 0 ? (
            <ul className="list-none space-y-1">
              {scores.map((entry) => {
                const game = games.find((g) => String(g.id) === String(entry.gameId));
                if (!game) return null;
                return (
                  <li
                    key={entry.gameId}
                    className="flex items-center gap-3 py-2 px-2 rounded-lg bg-white/5"
                  >
                    <img
                      src={`/img/games/${game.image}`}
                      alt=""
                      className="w-9 h-9 rounded-lg border-2 border-black flex-shrink-0"
                    />
                    <span className="flex-1 min-w-0 font-display font-bold text-nlg-text truncate">
                      {game.name}
                    </span>
                    <span
                      className="font-display font-black text-lg"
                      style={{ color: getGameColor(game) }}
                    >
                      {formatScoreForCard(game, entry.score)}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            // The sharer has logged nothing since the link went out. Say what
            // the site is instead of rendering an empty table.
            <p className="text-sm text-nlg-muted">
              Nothing logged yet today — which makes this a good moment to get there first.
            </p>
          )}

          {stillToPlay.length > 0 && (
            <p className="mt-3 text-sm text-nlg-muted">
              Still on {name}'s list today: {stillToPlay.map((g) => g.name).join(', ')}.
            </p>
          )}
        </div>

        <div className="px-3 py-3 border-t border-nlg-rule">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-bold text-nlg-gold transition-opacity duration-150 hover:opacity-80"
          >
            Play today's puzzles
            <img src="/img/right_arrow.png" alt="" className="w-4 h-4" />
          </Link>
          <p className="mt-2 text-sm text-nlg-muted">
            Play one and you'll show up on {name}'s scoreboard — no sign-up needed.
          </p>
        </div>
      </SectionBlock>
    </div>
  );
}

export default ShareLander;
